import { Controller } from "@hotwired/stimulus"
import * as pdfjs from "pdfjs-dist"

pdfjs.GlobalWorkerOptions.workerSrc = "/images/pdf.worker.min.js"

export default class extends Controller {
  static targets = ["track", "placeholder", "prevButton", "nextButton", "status"]
  static values = { url: String }

  async connect() {
    try {
      this.pdf = await pdfjs.getDocument(this.urlValue).promise
      this.pageCount = this.pdf.numPages
      this.renderedPages = new Map()

      if (this.hasPlaceholderTarget) this.placeholderTarget.remove()

      for (let i = 1; i <= this.pageCount; i++) {
        const slide = document.createElement("div")
        slide.className = "snap-center shrink-0 w-full flex justify-center"
        const canvas = document.createElement("canvas")
        canvas.className = "w-full h-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm aspect-[8.5/11]"
        slide.appendChild(canvas)
        this.trackTarget.appendChild(slide)
        if (i === 1) await this.renderPage(i, canvas)
      }

      this.observeSlides()
      this.updateButtons()
      this.statusTarget.textContent = `Page 1 of ${this.pageCount} — preview only, sign up for the full paper`
      this.showButtons()
    } catch (error) {
      if (this.hasPlaceholderTarget) {
        this.placeholderTarget.innerHTML = '<span class="text-sm text-gray-400 dark:text-gray-500 px-6 text-center">Preview unavailable — sign up to get the full paper.</span>'
      }
    }
  }

  observeSlides() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const index = Array.from(this.trackTarget.children).indexOf(entry.target)
        const pageNumber = index + 1
        const canvas = entry.target.querySelector("canvas")
        this.renderPage(pageNumber, canvas)
        this.statusTarget.textContent = `Page ${pageNumber} of ${this.pageCount} — preview only, sign up for the full paper`
      })
    }, { root: this.trackTarget, threshold: 0.5 })

    Array.from(this.trackTarget.children).forEach((slide) => this.observer.observe(slide))
  }

  async renderPage(pageNumber, canvas) {
    if (this.renderedPages.has(pageNumber)) return
    const page = await this.pdf.getPage(pageNumber)
    const baseViewport = page.getViewport({ scale: 1 })
    const targetWidth = this.trackTarget.clientWidth || baseViewport.width
    const scale = (targetWidth * (window.devicePixelRatio || 1)) / baseViewport.width
    const viewport = page.getViewport({ scale })
    canvas.width = viewport.width
    canvas.height = viewport.height
    const task = page.render({ canvasContext: canvas.getContext("2d"), viewport })
    this.renderedPages.set(pageNumber, task)
    await task.promise
  }

  showButtons() {
    if (this.pageCount < 2) return
    ;[this.prevButtonTarget, this.nextButtonTarget].forEach((btn) => {
      btn.classList.remove("hidden")
      btn.classList.add("flex")
    })
  }

  prev() {
    this.scrollByPage(-1)
  }

  next() {
    this.scrollByPage(1)
  }

  scrollByPage(direction) {
    const slide = this.trackTarget.children[0]
    if (!slide) return
    this.trackTarget.scrollBy({ left: direction * (slide.offsetWidth + 16), behavior: "smooth" })
  }

  updateButtons() {
    // Scroll-snap handles bounds naturally; nothing to disable
  }

  disconnect() {
    this.observer?.disconnect()
    this.pdf?.destroy()
  }
}
